import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { format, addDays, isAfter, isBefore } from "date-fns";
import { ar } from "date-fns/locale";
import { CalendarIcon, Car, Plus, Trash2, MapPin, User, DollarSign, Clock, CheckCircle, AlertCircle, Calculator } from "lucide-react";
import { toast } from "sonner";

// أسعار الخدمات
const PRICES = {
  vito: {
    pickup: 30,
    dropoff: 30,
    tour: 130
  },
  sprinter: {
    pickup: 35,
    dropoff: 35,
    tour: 140
  }
};

// الجولات المتاحة مع تفاصيل إضافية
const AVAILABLE_TOURS = [
  { name: "جولة طرابزون المدينة", duration: "نصف يوم", description: "زيارة المعالم التاريخية في المدينة" },
  { name: "جولة حيدر نبي", duration: "يوم كامل", description: "زيارة هضبة حيدر نبي والاستمتاع بالطبيعة" },
  { name: "جولة اوزنجول", duration: "يوم كامل", description: "زيارة بحيرة اوزنجول الساحرة" },
  { name: "جولة ايدر", duration: "يوم كامل", description: "استكشاف هضبة ايدر والمناظر الطبيعية" },
  { name: "جولة ريزي", duration: "يوم كامل", description: "زيارة مدينة ريزي ومزارع الشاي" },
  { name: "جولة همسي كوي", duration: "نصف يوم", description: "زيارة قرية همسي كوي التقليدية" }
];

// المطارات المتاحة
const AIRPORTS = [
  { id: "trabzon", name: "مطار طرابزون" },
  { id: "rize", name: "مطار ريزي آرتوين" }
];

interface Tour {
  id: string;
  name: string;
  date?: Date;
  duration?: string;
  description?: string;
}

interface BookingData {
  arrivalAirport: string;
  arrivalDate?: Date;
  departureAirport: string;
  departureDate?: Date;
  peopleCount: number;
  includePickup: boolean;
  includeDropoff: boolean;
  tours: Tour[];
  clientInfo: {
    name: string;
    phone: string;
    email: string;
    notes: string;
  };
}

export default function TurkishNorthTransport() {
  const [bookingData, setBookingData] = useState<BookingData>({
    arrivalAirport: "",
    arrivalDate: undefined,
    departureAirport: "", 
    departureDate: undefined,
    peopleCount: 1,
    includePickup: false,
    includeDropoff: false,
    tours: [],
    clientInfo: {
      name: "",
      phone: "",
      email: "",
      notes: ""
    }
  });

  const [totalPrice, setTotalPrice] = useState(0);
  const [vehicleType, setVehicleType] = useState<'vito' | 'sprinter'>('vito');
  const [selectedVehicleOverride, setSelectedVehicleOverride] = useState<'vito' | 'sprinter' | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // حساب نوع السيارة المناسب
  const recommendedVehicle = useMemo(() => {
    return bookingData.peopleCount <= 7 ? 'vito' : 'sprinter';
  }, [bookingData.peopleCount]);

  // تحديد نوع السيارة الفعلي (المختار أو المقترح)
  const actualVehicleType = selectedVehicleOverride || recommendedVehicle;

  useEffect(() => {
    setVehicleType(actualVehicleType);
  }, [actualVehicleType]);

  // حساب السعر الإجمالي مع تفاصيل أكثر
  const priceBreakdown = useMemo(() => {
    const breakdown = {
      pickup: bookingData.includePickup ? PRICES[vehicleType].pickup : 0,
      dropoff: bookingData.includeDropoff ? PRICES[vehicleType].dropoff : 0,
      tours: bookingData.tours.length * PRICES[vehicleType].tour,
      total: 0,
      savings: 0
    };
    
    breakdown.total = breakdown.pickup + breakdown.dropoff + breakdown.tours;
    
    // حساب توفير محتمل إذا اختار المستخدم الفيتو بدلاً من السبرنتر
    if (vehicleType === 'vito' && bookingData.peopleCount <= 7) {
      const sprinterTotal = 
        (bookingData.includePickup ? PRICES.sprinter.pickup : 0) +
        (bookingData.includeDropoff ? PRICES.sprinter.dropoff : 0) +
        (bookingData.tours.length * PRICES.sprinter.tour);
      breakdown.savings = sprinterTotal - breakdown.total;
    }
    
    return breakdown;
  }, [bookingData.includePickup, bookingData.includeDropoff, bookingData.tours.length, vehicleType, bookingData.peopleCount]);

  useEffect(() => {
    setTotalPrice(priceBreakdown.total);
  }, [priceBreakdown.total]);

  // التحقق من صحة التواريخ
  const validateDates = () => {
    const newErrors: Record<string, string> = {};
    
    if (bookingData.arrivalDate && bookingData.departureDate) {
      if (isAfter(bookingData.arrivalDate, bookingData.departureDate)) {
        newErrors.dates = "تاريخ الوصول لا يمكن أن يكون بعد تاريخ المغادرة";
      }
    }
    
    if (bookingData.arrivalDate && isBefore(bookingData.arrivalDate, new Date())) {
      newErrors.arrivalDate = "تاريخ الوصول لا يمكن أن يكون في الماضي";
    }
    
    if (bookingData.departureDate && isBefore(bookingData.departureDate, new Date())) {
      newErrors.departureDate = "تاريخ المغادرة لا يمكن أن يكون في الماضي";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    validateDates();
  }, [bookingData.arrivalDate, bookingData.departureDate]);

  const addTour = () => {
    const newTour: Tour = {
      id: Date.now().toString(),
      name: "",
      date: undefined
    };
    setBookingData(prev => ({
      ...prev,
      tours: [...prev.tours, newTour]
    }));
  };

  const removeTour = (tourId: string) => {
    setBookingData(prev => ({
      ...prev,
      tours: prev.tours.filter(tour => tour.id !== tourId)
    }));
  };

  const updateTour = (tourId: string, field: keyof Tour, value: any) => {
    setBookingData(prev => ({
      ...prev,
      tours: prev.tours.map(tour => {
        if (tour.id === tourId) {
          const updatedTour = { ...tour, [field]: value };
          // إضافة تفاصيل الجولة تلقائياً عند اختيار الاسم
          if (field === 'name') {
            const selectedTour = AVAILABLE_TOURS.find(t => t.name === value);
            if (selectedTour) {
              updatedTour.duration = selectedTour.duration;
              updatedTour.description = selectedTour.description;
            }
          }
          return updatedTour;
        }
        return tour;
      })
    }));
  };

  const handleSubmit = () => {
    // التحقق من صحة البيانات
    if (!validateDates()) {
      toast.error("يرجى التحقق من التواريخ المدخلة");
      return;
    }

    if (!bookingData.clientInfo.name || !bookingData.clientInfo.phone) {
      toast.error("يرجى ملء البيانات المطلوبة (الاسم والهاتف)");
      return;
    }

    if (!bookingData.includePickup && !bookingData.includeDropoff && bookingData.tours.length === 0) {
      toast.error("يرجى اختيار خدمة واحدة على الأقل");
      return;
    }

    // التحقق من صحة أرقام الهواتف
    const phoneRegex = /^[0-9+\-\s()]+$/;
    if (!phoneRegex.test(bookingData.clientInfo.phone)) {
      toast.error("يرجى إدخال رقم هاتف صحيح");
      return;
    }

    // التحقق من الإيميل إذا تم إدخاله
    if (bookingData.clientInfo.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(bookingData.clientInfo.email)) {
        toast.error("يرجى إدخال بريد إلكتروني صحيح");
        return;
      }
    }

    // إنشاء ملخص مفصل للحجز
    const bookingSummary = {
      ...bookingData,
      vehicleType: actualVehicleType,
      priceBreakdown,
      bookingDate: new Date(),
      bookingId: `TNT-${Date.now()}`
    };

    // محاكاة حفظ الحجز
    console.log("ملخص الحجز:", bookingSummary);
    
    toast.success(`تم تأكيد الحجز بنجاح! رقم الحجز: ${bookingSummary.bookingId}. العميل مسجل في النظام وطلبه قيد المراجعة.`);
    
    // إعادة تعيين النموذج
    setBookingData({
      arrivalAirport: "",
      arrivalDate: undefined,
      departureAirport: "",
      departureDate: undefined,
      peopleCount: 1,
      includePickup: false,
      includeDropoff: false,
      tours: [],
      clientInfo: {
        name: "",
        phone: "",
        email: "",
        notes: ""
      }
    });
    setSelectedVehicleOverride(null);
    setErrors({});
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Car className="h-8 w-8 text-primary" />
            نظام حساب مواصلات الشمال التركي
          </CardTitle>
          <p className="text-muted-foreground">
            نظام متطور لحساب تكلفة رحلات الشمال التركي مع إمكانية الاستقبال والتوديع والجولات اليومية
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* رسائل الخطأ */}
          {Object.keys(errors).length > 0 && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">يرجى تصحيح الأخطاء التالية:</span>
                </div>
                <ul className="mt-2 space-y-1">
                  {Object.values(errors).map((error, index) => (
                    <li key={index} className="text-sm text-destructive">• {error}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* تفاصيل الرحلة */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>مطار الوصول</Label>
              <Select value={bookingData.arrivalAirport} onValueChange={(value) => 
                setBookingData(prev => ({ ...prev, arrivalAirport: value, includePickup: value !== "none" && value !== "" }))
              }>
                <SelectTrigger>
                  <SelectValue placeholder="اختر مطار الوصول" />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border z-50">
                  <SelectItem value="none">لا يوجد</SelectItem>
                  {AIRPORTS.map(airport => (
                    <SelectItem key={airport.id} value={airport.id}>
                      {airport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>تاريخ الوصول</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !bookingData.arrivalDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {bookingData.arrivalDate ? format(bookingData.arrivalDate, "PPP", { locale: ar }) : "اختر التاريخ"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={bookingData.arrivalDate}
                    onSelect={(date) => setBookingData(prev => ({ ...prev, arrivalDate: date }))}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>مطار المغادرة</Label>
              <Select value={bookingData.departureAirport} onValueChange={(value) => 
                setBookingData(prev => ({ ...prev, departureAirport: value, includeDropoff: value !== "none" && value !== "" }))
              }>
                <SelectTrigger>
                  <SelectValue placeholder="اختر مطار المغادرة" />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border z-50">
                  <SelectItem value="none">لا يوجد</SelectItem>
                  {AIRPORTS.map(airport => (
                    <SelectItem key={airport.id} value={airport.id}>
                      {airport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>تاريخ المغادرة</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !bookingData.departureDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {bookingData.departureDate ? format(bookingData.departureDate, "PPP", { locale: ar }) : "اختر التاريخ"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={bookingData.departureDate}
                    onSelect={(date) => setBookingData(prev => ({ ...prev, departureDate: date }))}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* عدد الأشخاص */}
          <div className="space-y-2">
            <Label>عدد الأشخاص</Label>
            <Select 
              value={bookingData.peopleCount.toString()} 
              onValueChange={(value) => setBookingData(prev => ({ ...prev, peopleCount: parseInt(value) }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="اختر عدد الأشخاص" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border z-50">
                {Array.from({length: 14}, (_, i) => i + 1).map(num => (
                  <SelectItem key={num} value={num.toString()}>
                    {num} {num === 1 ? 'شخص' : 'أشخاص'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* اختيار نوع السيارة */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5 text-primary" />
                اختيار نوع السيارة
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedVehicleOverride('vito')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    actualVehicleType === 'vito' 
                      ? 'border-primary bg-primary/10 shadow-md' 
                      : 'border-border bg-muted/50 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">كرافيلا / فيتو</h4>
                      <p className="text-sm text-muted-foreground">حتى 7 أشخاص</p>
                      <p className="text-sm font-medium text-primary">جولة: {PRICES.vito.tour}$ | نقل: {PRICES.vito.pickup}$</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {actualVehicleType === 'vito' && (
                        <CheckCircle className="h-5 w-5 text-primary" />
                      )}
                      {recommendedVehicle === 'vito' && (
                        <Badge variant="secondary" className="text-xs">مقترحة</Badge>
                      )}
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setSelectedVehicleOverride('sprinter')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    actualVehicleType === 'sprinter' 
                      ? 'border-primary bg-primary/10 shadow-md' 
                      : 'border-border bg-muted/50 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">سبرنتر</h4>
                      <p className="text-sm text-muted-foreground">حتى 14 شخص</p>
                      <p className="text-sm font-medium text-primary">جولة: {PRICES.sprinter.tour}$ | نقل: {PRICES.sprinter.pickup}$</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {actualVehicleType === 'sprinter' && (
                        <CheckCircle className="h-5 w-5 text-primary" />
                      )}
                      {recommendedVehicle === 'sprinter' && (
                        <Badge variant="secondary" className="text-xs">مقترحة</Badge>
                      )}
                    </div>
                  </div>
                </button>
              </div>
              
              {bookingData.peopleCount <= 7 && actualVehicleType === 'sprinter' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    ✨ <strong>اختيار ممتاز!</strong> ستحصل على مساحة إضافية وراحة أكبر للرحلة
                  </p>
                </div>
              )}
              
              {priceBreakdown.savings > 0 && actualVehicleType === 'vito' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💰 <strong>توفير:</strong> اخترت الخيار الاقتصادي! وفرت {priceBreakdown.savings}$ مقارنة بالسبرنتر
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* الجولات */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-lg font-semibold">الجولات اليومية</Label>
              <Button onClick={addTour} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                إضافة جولة
              </Button>
            </div>

            {bookingData.tours.map((tour, index) => (
              <Card key={tour.id} className="border">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 space-y-2">
                      <Label>اليوم {index + 1}</Label>
                      <Select value={tour.name} onValueChange={(value) => updateTour(tour.id, 'name', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر الجولة" />
                        </SelectTrigger>
                        <SelectContent className="bg-background border border-border z-50">
                          {AVAILABLE_TOURS.map(tourOption => (
                            <SelectItem key={tourOption.name} value={tourOption.name}>
                              <div className="flex flex-col">
                                <span>{tourOption.name}</span>
                                <span className="text-xs text-muted-foreground">{tourOption.duration}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex-1 space-y-2">
                      <Label>تاريخ الجولة</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !tour.date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {tour.date ? format(tour.date, "PPP", { locale: ar }) : "اختر التاريخ"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={tour.date}
                            onSelect={(date) => updateTour(tour.id, 'date', date)}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTour(tour.id)}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* تفاصيل الجولة */}
                  {tour.name && (
                    <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">{tour.duration}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{tour.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ملخص الأسعار المفصل */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                ملخص الأسعار التفصيلي
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="text-xs">
                    {actualVehicleType === 'vito' ? 'كرافيلا / فيتو' : 'سبرنتر'}
                  </Badge>
                  {actualVehicleType !== recommendedVehicle && (
                    <Badge variant="secondary" className="text-xs">
                      اختيار مخصص
                    </Badge>
                  )}
                </div>
                
                {priceBreakdown.pickup > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>استقبال من المطار</span>
                    </div>
                    <span className="font-medium">{priceBreakdown.pickup}$</span>
                  </div>
                )}
                
                {priceBreakdown.dropoff > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>توديع إلى المطار</span>
                    </div>
                    <span className="font-medium">{priceBreakdown.dropoff}$</span>
                  </div>
                )}
                
                {bookingData.tours.length > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Car className="h-4 w-4 text-muted-foreground" />
                      <span>الجولات ({bookingData.tours.length} جولة)</span>
                    </div>
                    <span className="font-medium">{priceBreakdown.tours}$</span>
                  </div>
                )}
                
                {(priceBreakdown.pickup > 0 || priceBreakdown.dropoff > 0 || priceBreakdown.tours > 0) && (
                  <>
                    <Separator />
                    <div className="flex justify-between items-center text-lg font-bold text-primary">
                      <span>المجموع الكلي:</span>
                      <span>{priceBreakdown.total}$</span>
                    </div>
                    
                    {priceBreakdown.savings > 0 && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-green-800">وفرت مقارنة بالسبرنتر:</span>
                          <span className="font-bold text-green-600">{priceBreakdown.savings}$</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {priceBreakdown.total === 0 && (
                  <div className="text-center py-4 text-muted-foreground">
                    <Calculator className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>اختر الخدمات لرؤية السعر الإجمالي</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* معلومات العميل */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                معلومات العميل
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>الاسم *</Label>
                  <Input
                    value={bookingData.clientInfo.name}
                    onChange={(e) => setBookingData(prev => ({
                      ...prev,
                      clientInfo: { ...prev.clientInfo, name: e.target.value }
                    }))}
                    placeholder="اسم العميل"
                  />
                </div>

                <div className="space-y-2">
                  <Label>رقم الهاتف *</Label>
                  <Input
                    value={bookingData.clientInfo.phone}
                    onChange={(e) => setBookingData(prev => ({
                      ...prev,
                      clientInfo: { ...prev.clientInfo, phone: e.target.value }
                    }))}
                    placeholder="رقم الهاتف"
                  />
                </div>

                <div className="space-y-2">
                  <Label>البريد الإلكتروني</Label>
                  <Input
                    type="email"
                    value={bookingData.clientInfo.email}
                    onChange={(e) => setBookingData(prev => ({
                      ...prev,
                      clientInfo: { ...prev.clientInfo, email: e.target.value }
                    }))}
                    placeholder="البريد الإلكتروني"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>ملاحظات إضافية</Label>
                  <Textarea
                    value={bookingData.clientInfo.notes}
                    onChange={(e) => setBookingData(prev => ({
                      ...prev,
                      clientInfo: { ...prev.clientInfo, notes: e.target.value }
                    }))}
                    placeholder="أي ملاحظات أو طلبات خاصة..."
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* زر التأكيد */}
          <div className="space-y-4">
            {priceBreakdown.total > 0 && (
              <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">السعر الإجمالي</p>
                    <p className="text-3xl font-bold text-primary">{priceBreakdown.total}$</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {actualVehicleType === 'vito' ? 'كرافيلا / فيتو' : 'سبرنتر'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Button 
              onClick={handleSubmit}
              className="w-full h-12 text-lg font-semibold"
              size="lg"
              disabled={priceBreakdown.total === 0 || Object.keys(errors).length > 0}
            >
              <CheckCircle className="h-5 w-5 mr-2" />
              تأكيد الحجز
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              بعد التأكيد، سيتم مراجعة طلبك وسيتم التواصل معك قريباً
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}